/* eslint-disable no-undef */
import {
  EditOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
  InboxOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FileExcelOutlined,
  UploadOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Alert,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Upload,
  Form,
  Input,
  Select,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import { frenchDate } from "../../variables/functions";
import { searchProfil, searchUtilisateur } from "../../hooks/useSearch";
import ActionDropdown from "../production/components/ActionDropdown";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";
import { LOGGED_USER } from "../../variables/constants";

const { confirm } = Modal;

const TEMPLATE_HEADERS = [
  "Email",
  "Nom",
  "Prenom",
  "Fonction",
  "Genre",
  "Contact",
  "Adresse",
  "TypePersonne",
  "ProfilNom",
  "CodeAgence",
  "Actif",
];

const Utilisateurs = () => {
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [profils, setProfils] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [sortedName, setSortedName] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [dataScope, setDataScope] = useState("ACTIVE");
  const [count, setCount] = useState(0);
  const [statusCardSelection, setStatusCardSelection] = useState("active");
  const [importFile, setImportFile] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importConfirm, setImportConfirm] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    search: "",
    profil: null,
    actif: null,
    organisme: null,
  });
  const [filterForm] = Form.useForm();
  const [organismes, setOrganismes] = useState([]);

  const navigate = useNavigate();

  const importRows = useMemo(() => importPreview?.rows || [], [importPreview]);

  const invalidRowsCount = useMemo(() => {
    if (!importPreview?.rows) return 0;
    if (typeof importPreview.invalidRows === "number") {
      return importPreview.invalidRows;
    }
    return importPreview.rows.filter((row) => !row.valid).length;
  }, [importPreview]);

  const importSummary = useMemo(() => {
    if (!importPreview) return null;
    const total = importPreview?.totalRows ?? importRows.length ?? 0;
    const valid = importPreview?.validRows ?? importRows.filter((row) => row.valid).length;
    const invalid = importPreview?.invalidRows ?? invalidRowsCount;
    return {
      total,
      valid,
      invalid,
    };
  }, [importPreview, importRows, invalidRowsCount]);

  const blockIfInactive = (record, actionLabel) => {
    if (!record?.active) {
      toast.warn(
        `Impossible de ${actionLabel} : l'utilisateur est inactif. Activez-le d'abord.`
      );
      return true;
    }
    return false;
  };

  const isExcelFile = (file) => {
    if (!file) return false;
    return /\.xlsx?$/.test((file.name || "").toLowerCase());
  };

  const handleOpenImportModal = () => {
    resetImportState();
    setImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    resetImportState();
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportConfirm(null);
    setImportError(null);
    setPreviewLoading(false);
    setConfirmLoading(false);
  };

  const handleTemplateDownload = async () => {
    try {
      const ExcelJS = (await import("exceljs")).default;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Utilisateurs");
      sheet.columns = TEMPLATE_HEADERS.map((header) => ({
        header,
        key: header,
        width: Math.max(header.length + 4, 15),
      }));
      sheet.addRow([
        "jane.doe@exemple.com",
        "Doe",
        "Jane",
        "IT",
        "Feminin",
        "+22891542676",
        "Lomé",
        "PHYSIQUE",
        "Administrateur",
        "AG001",
        "OUI",
      ]);
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(2).font = { italic: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_import_utilisateurs.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      toast.error("Impossible de télécharger le template");
    }
  };

  const handleFileSelect = (file) => {
    if (!isExcelFile(file)) {
      toast.error("Format de fichier invalide. Choisissez un fichier .xlsx ou .xls");
      return Upload.LIST_IGNORE;
    }
    setImportFile(file);
    setImportPreview(null);
    setImportConfirm(null);
    setImportError(null);
    return false;
  };

  const handlePreviewImport = async () => {
    if (!importFile) return;
    setPreviewLoading(true);
    setImportError(null);
    setImportConfirm(null);
    const formData = new FormData();
    formData.append("file", importFile);
    try {
      const res = await axiosInstance.post("/api/users/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(res.data);
      toast.success("Prévisualisation effectuée");
    } catch (error) {
      console.log(error);
      const message =
        error?.response?.data?.message ||
        "Erreur lors de la prévisualisation. Vérifiez les en-têtes du fichier.";
      setImportError(message);
      toast.error(message);
    }
    setPreviewLoading(false);
  };

  const handleConfirmImport = async () => {
    if (!importFile || !importPreview || invalidRowsCount > 0) return;
    setConfirmLoading(true);
    setImportError(null);
    const formData = new FormData();
    formData.append("file", importFile);
    try {
      const res = await axiosInstance.post("/api/users/import/confirm", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportConfirm(res.data);
      setImportPreview(res.data);
      toast.success("Import confirmé");
      fetchData({ pagination });
    } catch (error) {
      console.log(error);
      const message =
        error?.response?.data?.message ||
        "Erreur lors de la confirmation. Veuillez réessayer.";
      setImportError(message);
      toast.error(message);
    }
    setConfirmLoading(false);
  };

  const buildFilterPayload = (overrides = {}) => {
    const filters = {};

    const effectiveStatus =
      overrides.actif !== undefined
        ? overrides.actif
        : selectedStatus ?? advancedFilters.actif;

    if (effectiveStatus !== null && effectiveStatus !== undefined) {
      filters.actif = effectiveStatus;
    }

    const effectiveProfil =
      overrides.profil !== undefined ? overrides.profil : advancedFilters.profil;
    if (effectiveProfil) {
      filters.profil = effectiveProfil;
    }

    const effectiveOrganisme =
      overrides.organisme !== undefined ? overrides.organisme : advancedFilters.organisme;
    if (effectiveOrganisme) {
      filters.organisme = effectiveOrganisme;
    }

    const searchTerm =
      overrides.search !== undefined ? overrides.search : advancedFilters.search;
    if (searchTerm) {
      filters.search = searchTerm;
    }

    return {
      ...filters,
      ...overrides,
    };
  };

  const applyStatusQuickFilter = (statusValue, scope = "ALL") => {
    setStatusCardSelection(statusValue === true ? "active" : statusValue === false ? "inactive" : "all");
    setSelectedStatus(statusValue);
    setDataScope(scope);
    setAdvancedFilters((prev) => ({ ...prev, actif: statusValue }));

    const filters = buildFilterPayload({ actif: statusValue });
    if (statusValue === null || statusValue === undefined) {
      delete filters.actif;
    }

    const nextPagination = { ...pagination, current: 1 };
    setPagination(nextPagination);
    fetchData({
      pagination: nextPagination,
      filters,
      scopeOverride: scope,
    });
  };

  const handleOpenFiltersModal = () => {
    filterForm.setFieldsValue({
      search: advancedFilters.search || "",
      profil: advancedFilters.profil || undefined,
      actif:
        selectedStatus !== null && selectedStatus !== undefined
          ? selectedStatus
          : advancedFilters.actif,
      scope: dataScope || "ALL",
      organisme: advancedFilters.organisme || undefined,
    });
    setFiltersModalOpen(true);
  };

  const handleApplyAdvancedFilters = async () => {
    try {
      const values = await filterForm.validateFields();
      const nextFilters = {
        search: values.search ? values.search.trim() : "",
        profil: values.profil || null,
        actif: values.actif === undefined ? null : values.actif,
        organisme: values.organisme || null,
      };
      const nextScope = values.scope || dataScope || "ALL";
      const nextPagination = { ...pagination, current: 1 };

      setAdvancedFilters(nextFilters);
      setSelectedStatus(nextFilters.actif);
      setDataScope(nextScope);
      setStatusCardSelection(
        nextFilters.actif === true ? "active" : nextFilters.actif === false ? "inactive" : "all"
      );
      setPagination(nextPagination);

      fetchData({
        pagination: nextPagination,
        filters: buildFilterPayload(nextFilters),
        scopeOverride: nextScope,
      });
      setFiltersModalOpen(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleResetAdvancedFilters = () => {
    const resetPagination = { ...pagination, current: 1 };
    setAdvancedFilters({ search: "", profil: null, actif: null, organisme: null });
    setSelectedStatus(null);
    setStatusCardSelection("all");
    setDataScope("ALL");
    setPagination(resetPagination);
    filterForm.resetFields();
    fetchData({
      pagination: resetPagination,
      filters: buildFilterPayload({ search: "", profil: null, actif: null }),
      scopeOverride: "ALL",
    });
    setFiltersModalOpen(false);
  };

  const columns = [
    {
      title: "Nom & Prénom(s)",
      dataIndex: "name",
      key: "name",
      searchable: true,
      showSorterTooltip: {
        target: "full-header",
        title: sortedName ? "Annuler le tri" : "Trier par nom",
      },
      sortDirections: ["descend"],
      sorter: () => {
        setSortedName(!sortedName);
      },
      render: (name, record) => (
        <div className="d-flex align-items-center">
          <div className="avatar-wrapper">
            <div className="avatar avatar-sm me-3">
              {record.photo_url ? (
                <img
                  src={`path/to/assets/img/avatars/${Math.floor(Math.random() * 11) + 1
                    }.png`}
                  alt="Avatar"
                  className="rounded-circle"
                />
              ) : (
                <span
                  className={
                    record?.active
                      ? "avatar-initial rounded-circle bg-label-success"
                      : "avatar-initial rounded-circle bg-label-danger"
                  }
                >
                  {name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
              )}
            </div>
          </div>
          <div>
            <a
              href={`/details-utilisateur/${record.codeId}`}
              className="text-heading text-truncate"
            >
              {name}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
      searchable: true,
      sorter: (a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }),
      sortDirections: ["ascend"],
    },
    {
      title: "Prénom",
      dataIndex: "prenom",
      key: "prenom",
      searchable: true,
      sorter: (a, b) =>
        (a.prenom || "").localeCompare(b.prenom || "", "fr", {
          sensitivity: "base",
        }),
      sortDirections: ["ascend"],
    },
    {
      title: "Adresse mail",
      dataIndex: "email",
      key: "email",
      searchable: true,
      sorter: (a, b) =>
        (a.email || "").localeCompare(b.email || "", "fr", {
          sensitivity: "base",
        }),
      sortDirections: ["ascend"],
    },
    {
      title: "Fonction",
      dataIndex: "fonction",
      key: "fonction",
      searchable: true,
    },
    {
      title: "Profil",
      dataIndex: "profil",
      key: "profil",
      searchable: true,
      filterType: "select",
      filterOptions: (profils || []).map((profil) => ({
        label: profil.nom,
        value: profil.nom,
      })),
      render: (role, record) => (
        <Tooltip
          title={
            rightsService("HAC056") ? "Cliquez pour modifier le profil" : ""
          }
        >
          <Tag
            color="orange"
            onClick={() => {
              if (!rightsService("HAC056")) return;
              if (blockIfInactive(record, "modifier le profil")) return;
              navigate(`/modifier-profil/${record.data.profil.codeId}`);
            }}
          >
            <Badge color={"blue"} text={role} />
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: "Date de derniere connexion",
      dataIndex: "lastConnexion",
      key: "lastConnexion",
      render: (lastConnexion, record) => (
        <Tag>
          {lastConnexion
            ? frenchDate(lastConnexion)
            : frenchDate(record?.data?.updatedAt || record.createdAt)}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "active",
      key: "active",
      filterType: "select",
      filterOptions: [
        { label: "Actif", value: true },
        { label: "Inactif", value: false },
      ],
      render: (actif) => (
        <Tag color={actif ? "green" : "red"}>
          {actif ? "Actif" : "Inactif"}
        </Tag>
      ),
      exportValue: (row) => (row.active ? "Actif" : "Inactif"),
    },
    {
      title: "Actions",
      key: "action",
      hideInExport: true,
      render: (_, record) => {
        const actions = [
          {
            key: "details",
            label: "Détails",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/details-utilisateur/${record.codeId}`),
          },
          rightsService("HPE039") && {
            key: "activate",
            label: record.active ? "Désactiver" : "Activer",
            onClick: () => showActivateConfirm(record.codeId, record.active),
          },
          rightsService("HPE041") &&
          record.active && {
            key: "edit",
            label: "Modifier",
            icon: <EditOutlined />,
            onClick: () => {
              if (blockIfInactive(record, "modifier")) return;
              navigate(`/details-utilisateur/${record.codeId}`);
            },
          },
          rightsService("HPE041") &&
          record.active && {
            key: "reset",
            label: "Réinitialisation",
            icon: <ReloadOutlined />,
            onClick: () => {
              if (blockIfInactive(record, "réinitialiser")) return;
              showReinitialisationConfirm(record.email, record.active);
            },
          },
        ].filter(Boolean);

        return (
          <ActionDropdown
            trigger={<Button icon={<SettingOutlined />} />}
            actions={actions}
          />
        );
      },
    },
  ];

  const tableToolbarExtras = (
    <Space wrap>
      <Button
        icon={<FilterOutlined />}
        onClick={handleOpenFiltersModal}
        style={{
          borderRadius: "8px",
          height: "40px",
        }}
      >
        Filtres avancés
      </Button>

      {rightsService("HPE038") && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/ajouter-utilisateur")}
          style={{
            background: COLORS.gradient,
            border: "none",
            borderRadius: "8px",
            height: "40px",
            fontWeight: "500",
            boxShadow: "0 4px 12px rgba(24, 144, 255, 0.25)"
          }}
        >
          Créer un nouvel utilisateur
        </Button>
      )}

      <Button
        icon={<UploadOutlined />}
        onClick={handleOpenImportModal}
        style={{
          borderRadius: "8px",
          height: "40px",
        }}
      >
        Importer (Excel)
      </Button>
    </Space>
  );

  const renderImportStatus = (row) => {
    const hasErrors = Array.isArray(row?.errors) && row.errors.length > 0;
    if (importConfirm) {
      const created = row?.createdUserCodeId && !hasErrors;
      return (
        <Tag color={created ? "green" : "red"} icon={created ? <CheckCircleFilled /> : <CloseCircleFilled />}>
          {created ? "Créé" : "Erreur"}
        </Tag>
      );
    }
    const ok = row?.valid && !hasErrors;
    return (
      <Tag color={ok ? "green" : "red"} icon={ok ? <CheckCircleFilled /> : <CloseCircleFilled />}>
        {ok ? "OK" : "Erreur"}
      </Tag>
    );
  };

  const importColumns = [
    {
      title: "#",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 70,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
    },
    {
      title: "Prénom",
      dataIndex: "prenom",
      key: "prenom",
    },
    {
      title: "Profil",
      dataIndex: "profilNom",
      key: "profilNom",
    },
    {
      title: "Agence",
      dataIndex: "codeAgence",
      key: "codeAgence",
    },
    {
      title: "Actif",
      dataIndex: "actif",
      key: "actif",
      render: (actif) => (
        <Tag color={actif ? "green" : "red"}>{actif ? "Oui" : "Non"}</Tag>
      ),
    },
    {
      title: "Statut",
      key: "status",
      render: (_, record) => renderImportStatus(record),
    },
    {
      title: "Erreurs",
      dataIndex: "errors",
      key: "errors",
      render: (errors) =>
        errors && errors.length ? (
          <Tooltip title={errors.join(", ")}>
            <span>{errors.join(" | ")}</span>
          </Tooltip>
        ) : (
          <Tag color="green">Aucune</Tag>
        ),
    },
    {
      title: "Code créé",
      dataIndex: "createdUserCodeId",
      key: "createdUserCodeId",
      render: (value) => value || "-",
    },
  ];

  const canConfirmImport = !!(
    importPreview &&
    invalidRowsCount === 0 &&
    importRows.length &&
    !previewLoading
  );
  const confirmSummary = importConfirm
    ? {
      total: importConfirm?.totalRows ?? importRows.length ?? 0,
      created: importConfirm?.createdRows ?? 0,
      failed: importConfirm?.failedRows ?? 0,
    }
    : null;

  const handleChange = (paginationArg, filters, sorter) => {
    setCurrentPage(paginationArg.current);
    setPageSize(paginationArg.pageSize);
    setPagination(paginationArg);
  };

  const fetchData = (params = {}) => {
    setLoading(true);
    const scope = params.scopeOverride || dataScope;
    const paginationParams = params.pagination || pagination;
    const filterParams =
      params.filters !== undefined ? { ...params.filters } : buildFilterPayload();

    Object.keys(filterParams).forEach((key) => {
      if (filterParams[key] === null || filterParams[key] === undefined) {
        delete filterParams[key];
      }
    });

    axiosInstance
      .get(`/api/utilisateur`, {
        params: {
          page: (paginationParams.current || 1) - 1,
          size: paginationParams.pageSize || pageSize,
          sortedName: sortedName,
          ...filterParams,
          ...(scope === "ALL" ? {} : { activeData: scope === "ACTIVE" }),
        },
      })
      .then((res) => {
        const usr = [];
        res.data?.content?.forEach((user, index) => {
          usr.push({
            id: index + 1,
            nom: user.personne?.nom,
            prenom: user.personne?.prenom,
            name: `${user.personne?.nom} ${user.personne?.prenom}`,
            username: user.username,
            fonction: user.personne?.fonction,
            email: user?.email,
            profil: user.profil?.nom,
            active: user.active,
            createdAt: user?.createdAt,
            lastConnexion: user?.lastConnexion,
            data: user,
            codeId: user.codeId,
          });
        });
        setPagination({
          ...paginationParams,
          total: res.data?.totalElements,
        });
        setUtilisateurs(usr);
        setLoading(false);
        // setTotalUtilisateurs(res.data?.totalElements);

        // setActiveUtilisateurs(usr.filter((u) => u.active).length);
        // setNonActiveUsers(usr.filter((u) => !u.active).length);
        // setPendingUsers(0);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });

    axiosInstance
      .get(`/api/utilisateur/stats`, {
        params: {
          ...filterParams,
          ...(scope === "ALL" ? {} : { activeData: scope === "ACTIVE" }),
        },
      }).then((res) => {
        console.log("Stats utilisateurs: ", res.data);
        setCount(res.data);
      }).catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData({
      pagination,
    });
  }, [currentPage, pageSize, sortedName]);

  useEffect(() => {
    searchProfil("")
      .then((res) => setProfils(res.data?.content))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL") {
      axiosInstance
        .get(`/api/organisme/all/assurances-pool`)
        .then((res) => setOrganismes(res.data || []))
        .catch((err) => console.log(err));
    }
  }, []);

  const showActivateConfirm = (utilisateurCodeId, status) => {
    confirm({
      title: status
        ? "Etes-vous sûr de vouloir désactiver cet utilisateur?"
        : "Etes-vous sûr de vouloir activer cet utilisateur?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action sera historisée et donc est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        activateUtilisateur(utilisateurCodeId, status);
      },
    });
  };

  const showReinitialisationConfirm = (email, isActive) => {
    if (!isActive) {
      toast.warn(
        "Impossible de réinitialiser : utilisateur inactif. Activez-le d'abord."
      );
      return;
    }
    confirm({
      title: "Etes-vous sûr de vouloir de réinitialiser ce compte utilisateur?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action sera historisée et donc est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        reinitUtilisateur(email);
      },
    });
  };

  const activateUtilisateur = (utilisateurCodeId, status) => {
    axiosInstance
      .get(`/api/utilisateur/activate/${utilisateurCodeId}`)
      .then(() => {
        window.location.reload();
        toast(
          status
            ? "Utilisateur désactivé avec succès"
            : "Utilisateur activé avec succès"
        );
      })
      .catch((err) => {
        console.log(err);
        toast("Erreur lors de l'activation");
      });
  };

  const reinitUtilisateur = (email) => {
    axiosInstance
      .get(`/api/utilisateur/reinitialisation/${email}`)
      .then(() => {
        window.location.reload();
        toast("Utilisateur réinitialisé avec succès");
      })
      .catch((err) => {
        console.log(err);
        toast("Erreur lors de la réinitialisation");
      });
  };

  const renderSearchResults = (results) => {
    return <div className="px-3 py-2">
      <h6 className="text-muted text-uppercase mb-2"></h6>
      {results.map((item, index) => (
        <a
          key={index}
          href={`/details-utilisateur/${item.codeId}`}
          className="dropdown-item d-flex align-items-center gap-2 py-1"
        >
          <span className="text-truncate">
            {item?.personne?.nom} {item?.personne?.prenom} -{" "}
            {item?.username}
          </span>
        </a>
      ))}
    </div>;
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchUtilisateur(searchValue).then((res) => {
        setSearchResults(res.data.content);
      }).catch((err) => {
        console.log(err);
      });
    }
  }, [searchValue]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"UTILISATEURS"} />
        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* Cartes statistiques modernes */}
              <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
                <Col xs={24} sm={12} lg={8}>
                  <StatCard
                    title="Total Utilisateurs"
                    subtitle="Tous les utilisateurs"
                    value={count?.total}
                    icon="bx bx-user"
                    iconBg={COLORS.gradient}
                    gradient={COLORS.gradient}
                    onClick={() => applyStatusQuickFilter(null, "ALL")}
                    active={statusCardSelection === "all"}
                  />
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <StatCard
                    title="Utilisateurs Actifs"
                    subtitle="Personnel actif"
                    value={count?.active}
                    icon="bx bx-user-check"
                    iconBg="linear-gradient(135deg, #fa8c16 0%, #faad14 100%)"
                    gradient="linear-gradient(135deg, #fa8c16 0%, #faad14 100%)"
                    onClick={() => applyStatusQuickFilter(true, "ALL")}
                    active={statusCardSelection === "active"}
                  />
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <StatCard
                    title="Utilisateurs Inactifs"
                    subtitle="Personnel inactif"
                    value={count?.inactive}
                    icon="bx bx-user-x"
                    iconBg="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                    gradient="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                    onClick={() => applyStatusQuickFilter(false, "ALL")}
                    active={statusCardSelection === "inactive"}
                  />
                </Col>
              </Row>

              {/* Table moderne */}
              <Card
                bordered={false}
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  overflow: "hidden"
                }}
              >
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={utilisateurs}
                  onChange={handleChange}
                  loading={loading}
                  showInlineControls={false}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} utilisateurs`,
                    style: { marginTop: "24px" }
                  }}
                  rowKey="codeId"
                  exportFileName="utilisateurs"
                  toolbarExtras={tableToolbarExtras}
                />
              </Card>
            </div>

            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      <Modal
        title="Filtres avancés"
        open={filtersModalOpen}
        onCancel={() => setFiltersModalOpen(false)}
        destroyOnClose
        footer={[
          <Button key="reset" onClick={handleResetAdvancedFilters}>
            Réinitialiser
          </Button>,
          <Button key="cancel" onClick={() => setFiltersModalOpen(false)}>
            Annuler
          </Button>,
          <Button key="apply" type="primary" onClick={handleApplyAdvancedFilters}>
            Appliquer
          </Button>,
        ]}
      >
        <Form layout="vertical" form={filterForm}>
          <Form.Item name="search" label="Recherche (nom, email, identifiant)">
            <Input allowClear placeholder="Nom, email, username, contact..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="profil" label="Profil">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choisir un profil"
                  options={(profils || []).map((profil) => ({
                    label: profil.nom,
                    value: profil.codeId || profil.nom,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="actif" label="Statut">
                <Select
                  allowClear
                  placeholder="Tous les statuts"
                  options={[
                    { label: "Actif", value: true },
                    { label: "Inactif", value: false },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" ? (
            <Form.Item name="organisme" label="Organisme">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Choisir un organisme"
                options={(organismes || []).map((org) => ({
                  label: org.raisonSociale || org.nom || org.name,
                  value: org.code,
                }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="scope" label="Portée des données">
            <Select
              options={[
                { label: "Afficher uniquement les actifs", value: "ACTIVE" },
                { label: "Afficher tous les utilisateurs", value: "ALL" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import des utilisateurs (Excel)"
        open={importModalOpen}
        onCancel={handleCloseImportModal}
        width={1000}
        destroyOnClose
        footer={[
          <Button key="template" icon={<FileExcelOutlined />} onClick={handleTemplateDownload}>
            Télécharger un exemple
          </Button>,
          <Button
            key="preview"
            type="primary"
            onClick={handlePreviewImport}
            disabled={!importFile}
            loading={previewLoading}
          >
            Prévisualiser
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleConfirmImport}
            disabled={!canConfirmImport}
            loading={confirmLoading}
          >
            Confirmer l'import
          </Button>,
        ]}
      >
        <Alert
          message="Téléversez un fichier Excel avec les en-têtes: Email, Nom, Prenom, Genre, Contact, Adresse, TypePersonne, ProfilNom, CodeAgence, Actif."
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />
        <Upload.Dragger
          multiple={false}
          accept=".xlsx,.xls"
          beforeUpload={handleFileSelect}
          showUploadList={false}
          disabled={previewLoading || confirmLoading}
          style={{ borderRadius: "12px" }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Déposez le fichier ici ou cliquez pour sélectionner
          </p>
          <p className="ant-upload-hint">
            Extensions acceptées : .xlsx, .xls
          </p>
        </Upload.Dragger>
        {importFile ? (
          <div style={{ marginTop: 8 }}>
            <Tag color="blue">{importFile.name}</Tag>
          </div>
        ) : null}

        {importError ? (
          <Alert
            style={{ marginTop: 12 }}
            message={importError}
            type="error"
            showIcon
          />
        ) : null}

        {importSummary ? (
          <div style={{ marginTop: 12 }}>
            <Space wrap>
              <Tag color="geekblue">Total : {importSummary.total}</Tag>
              <Tag color="green">Valides : {importSummary.valid}</Tag>
              <Tag color="red">Invalides : {importSummary.invalid}</Tag>
              {confirmSummary ? (
                <>
                  <Tag color="green">Créés : {confirmSummary.created}</Tag>
                  <Tag color="red">Échoués : {confirmSummary.failed}</Tag>
                </>
              ) : null}
            </Space>
          </div>
        ) : null}

        {importSummary?.invalid > 0 ? (
          <Alert
            style={{ marginTop: 12 }}
            message="Certaines lignes contiennent des erreurs, corrigez le fichier puis relancez la prévisualisation."
            type="warning"
            showIcon
          />
        ) : null}

        {importPreview ? (
          <Table
            style={{ marginTop: 16 }}
            columns={importColumns}
            dataSource={importRows}
            rowKey={(record) => record?.rowNumber || `${record.email}-${record.rowNumber}`}
            pagination={{ pageSize: 8 }}
            scroll={{ x: true }}
          />
        ) : null}
      </Modal>


      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div >
  );
};

export default Utilisateurs;
